import Highcharts from 'highcharts';
import { SwHeightZone } from '../../../@types/swHeight/types';

export type DepthSeriesPoint = [number, number | null];

/** Pairs each value with its depth; rows without a depth are dropped. */
export const toDepthSeries = (
  depth: (number | null)[],
  values: (number | null)[] | undefined
): DepthSeriesPoint[] => {
  if (!values) return [];
  const points: DepthSeriesPoint[] = [];
  depth.forEach((d, index) => {
    if (d === null || d === undefined) return;
    points.push([d, values[index] ?? null]);
  });
  return points;
};

export const depthRange = (depth: (number | null)[]) => {
  const valid = depth.filter((d): d is number => d !== null && d !== undefined);
  if (valid.length === 0) return { min: 0, max: 1 };
  return { min: Math.min(...valid), max: Math.max(...valid) };
};

/**
 * Maps a sample MD onto the displayed depth reference by linear
 * interpolation over the log's MD→TVDSS pairs (the sample data only
 * carries MD). Outside the logged interval the boundary MD−TVDSS
 * offset is applied.
 */
export const mdToReference = (
  md: (number | null)[],
  reference: (number | null)[],
  sampleMd: number
): number | null => {
  const pairs: [number, number][] = [];
  md.forEach((m, index) => {
    const r = reference[index];
    if (m !== null && m !== undefined && r !== null && r !== undefined) {
      pairs.push([m, r]);
    }
  });
  if (pairs.length === 0) return null;
  if (pairs.length === 1) return sampleMd - (pairs[0][0] - pairs[0][1]);
  if (sampleMd <= pairs[0][0]) {
    return sampleMd - (pairs[0][0] - pairs[0][1]);
  }
  const last = pairs[pairs.length - 1];
  if (sampleMd >= last[0]) {
    return sampleMd - (last[0] - last[1]);
  }
  for (let i = 1; i < pairs.length; i += 1) {
    const [m0, r0] = pairs[i - 1];
    const [m1, r1] = pairs[i];
    if (sampleMd >= m0 && sampleMd <= m1) {
      if (m1 === m0) return r0;
      const t = (sampleMd - m0) / (m1 - m0);
      return r0 + t * (r1 - r0);
    }
  }
  return null;
};

export const ROCK_TYPE_FALLBACK_COLORS = [
  '#b0bec5', // 0 - non reservoir (grey)
  '#1e88e5',
  '#26a69a',
  '#fbc02d',
  '#fb8c00',
  '#e53935',
  '#8e24aa'
];

export const rockTypeColor = (
  rockType: number,
  zones: SwHeightZone[]
): string => {
  if (rockType <= 0) return ROCK_TYPE_FALLBACK_COLORS[0];
  const zone = zones[rockType - 1];
  if (zone?.color) return zone.color;
  return ROCK_TYPE_FALLBACK_COLORS[
    rockType % ROCK_TYPE_FALLBACK_COLORS.length
  ];
};

/**
 * Collapses the per-row rock-type array into contiguous depth intervals and
 * renders them as plot bands on the (vertical) depth axis.
 */
export const buildRockTypeBands = (
  depth: (number | null)[],
  rockType: (number | null)[],
  zones: SwHeightZone[]
): Highcharts.XAxisPlotBandsOptions[] => {
  const bands: Highcharts.XAxisPlotBandsOptions[] = [];
  let current: { rt: number; from: number; to: number } | null = null;

  const flush = () => {
    if (!current) return;
    bands.push({
      from: current.from,
      to: current.to,
      color: rockTypeColor(current.rt, zones),
      label: {
        text: current.rt > 0 ? `RT ${current.rt}` : '',
        style: { fontSize: '9px', color: '#ffffff' }
      }
    });
    current = null;
  };

  for (let index = 0; index < depth.length; index += 1) {
    const d = depth[index];
    const rt = rockType[index];
    if (d === null || d === undefined || rt === null || rt === undefined) {
      flush();
      continue;
    }
    const next = depth[index + 1];
    const halfStep =
      next !== null && next !== undefined ? (next - d) / 2 : 0.25;
    const from = d - halfStep;
    const to = d + halfStep;
    if (current && current.rt === rt) {
      current.to = to;
    } else {
      flush();
      current = { rt, from, to };
    }
  }
  flush();
  return bands;
};

/** How a curve is drawn, which the header echoes as a swatch shape. */
export type CurveShape = 'line' | 'points';

/**
 * One header pill: what a curve is and what it is scaled to.
 *
 * A presentation descriptor deliberately kept parallel to the Highcharts
 * series rather than derived from it -- the series carry resolved points (the
 * core plugs even carry their own depth basis), and threading label/colour/
 * scale metadata back out of them would be guesswork.
 */
export interface TrackCurveHeader {
  id: string;
  label: string;
  color: string;
  shape: CurveShape;
  unit?: string;
  /** Left/right scale labels. Null renders as an em dash. */
  min: number | null;
  max: number | null;
}

/** Finite min/max over a set of depth series, ignoring gaps. */
export const valueRange = (
  series: DepthSeriesPoint[]
): { min: number | null; max: number | null } => {
  let min: number | null = null;
  let max: number | null = null;
  series.forEach(([, value]) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return;
    }
    if (min === null || value < min) min = value;
    if (max === null || value > max) max = value;
  });
  return { min, max };
};

/**
 * A rounded, human-readable scale bound.
 *
 * Track scales are read at a glance, so `0 – 4` beats `0 – 3.9612`. Rounds
 * outward so the rounded bound never clips real data.
 */
export const niceBound = (value: number, direction: 'up' | 'down'): number => {
  if (value === 0 || !Number.isFinite(value)) return 0;
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(value))));
  const step = magnitude / 2;
  return direction === 'up'
    ? Math.ceil(value / step) * step
    : Math.floor(value / step) * step;
};

/**
 * Nearest index in an ascending depth array, for reading curve values under
 * the crosshair. Binary search -- this runs on every pointer move.
 */
export const nearestDepthIndex = (
  depth: (number | null)[],
  target: number
): number | null => {
  let low = 0;
  let high = depth.length - 1;
  if (high < 0) return null;
  while (low < high) {
    const mid = (low + high) >> 1;
    const value = depth[mid];
    if (value === null || value === undefined) {
      // Gaps are rare; step past them rather than complicating the search.
      low = mid + 1;
      continue;
    }
    if (value < target) low = mid + 1;
    else high = mid;
  }
  // `low` is the first index at or past the target; check its neighbour.
  const before = low > 0 ? depth[low - 1] : null;
  const at = depth[low];
  if (at === null || at === undefined) return before === null ? null : low - 1;
  if (before === null || before === undefined) return low;
  return Math.abs(before - target) <= Math.abs(at - target) ? low - 1 : low;
};
